/**
 * SequencesPanel — v2.3.0
 *
 * Admin-only management of WhatsApp lead sequences.
 *
 * Layout :
 *   - Header explanatory + "+ Nouvelle séquence" button
 *   - List of sequences for the active project (+ globales)
 *     · each row shows: nom, projet, nombre d'étapes, statut actif, default
 *     · click → opens the SequenceEditDialog
 *
 * The edit dialog handles : nom, description, is_default, active, steps[].
 * Each step has : delay_hours, kind (whatsapp_text / template / log_note /
 * wait), body / template_name, stop_if.
 *
 * Intentionally not a full no-code workflow builder — just enough to cover
 * "welcome J+0 → relance J+3 → catalogue J+7 → RDV J+14" without drag-drop.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  GitBranch,
  Plus,
  Trash2,
  Edit2,
  X,
  Save,
  Clock,
  MessageCircle,
  FileText,
  PauseCircle,
  PlayCircle,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { sequencesApi } from "@/lib/crmApi";

const STEP_KINDS = [
  { value: "whatsapp_text", label: "WhatsApp · texte libre", icon: MessageCircle },
  { value: "whatsapp_template", label: "WhatsApp · template Meta", icon: MessageCircle },
  { value: "log_note", label: "Note interne (historique)", icon: FileText },
  { value: "wait", label: "Attendre uniquement (no-op)", icon: Clock },
];

const STOP_OPTIONS = [
  { value: "status_won_or_lost", label: "Lead gagné ou perdu" },
  { value: "lead_replied", label: "Lead a répondu (48h)" },
  { value: "status_won", label: "Lead gagné uniquement" },
  { value: "status_lost", label: "Lead perdu uniquement" },
];

export default function SequencesPanel({ projet }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await sequencesApi.list(projet?.id || null);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Impossible de charger les séquences");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [projet?.id]);

  const onCreate = () => {
    setEditTarget(null);
    setEditOpen(true);
  };

  const onEdit = (seq) => {
    setEditTarget(seq);
    setEditOpen(true);
  };

  const onDelete = async (seq) => {
    if (!window.confirm(`Supprimer la séquence "${seq.nom}" ?`)) return;
    try {
      await sequencesApi.remove(seq.id);
      toast.success("Séquence supprimée");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail || "Échec";
      toast.error(d);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-violet-200/60 bg-gradient-to-br from-violet-50/40 to-card p-5 shadow-soft dark:border-violet-900/30 dark:from-violet-950/20 dark:to-card">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
            <GitBranch className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-base font-bold">
              Séquences automatiques
              <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
                v2.3
              </span>
            </h3>
            <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
              Programme une suite d'actions automatiques quand un lead est
              créé : welcome → relance J+3 → catalogue → proposition RDV…
              Conditions d'arrêt (lead a répondu, statut gagné/perdu) gérées
              automatiquement.
            </p>
          </div>
        </div>
        <Button
          onClick={onCreate}
          className="gap-1.5 bg-violet-600 text-white hover:bg-violet-700"
          data-testid="seq-create-btn"
        >
          <Plus className="h-4 w-4" />
          Nouvelle séquence
        </Button>
      </div>

      {loading ? (
        <div className="rounded-lg border border-border/60 bg-muted/20 p-6 text-center text-sm text-muted-foreground">
          Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 p-6 text-center">
          <p className="text-sm font-semibold">Aucune séquence configurée</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Crée ta première séquence pour automatiser le suivi des nouveaux leads.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((seq) => (
            <SequenceRow
              key={seq.id}
              seq={seq}
              onEdit={() => onEdit(seq)}
              onDelete={() => onDelete(seq)}
            />
          ))}
        </div>
      )}

      <SequenceEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        target={editTarget}
        projet={projet}
        onSaved={() => {
          setEditOpen(false);
          load();
        }}
      />
    </div>
  );
}

function SequenceRow({ seq, onEdit, onDelete }) {
  const stepCount = (seq.steps || []).length;
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-card p-3">
      <div className="flex min-w-0 items-center gap-3">
        <GitBranch className="h-4 w-4 flex-none text-violet-600" />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-heading font-semibold truncate">{seq.nom}</span>
            {seq.is_default && (
              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                Défaut
              </span>
            )}
            {!seq.active && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-muted-foreground">
                Inactive
              </span>
            )}
            {seq.projet_id ? null : (
              <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                Globale
              </span>
            )}
          </div>
          {seq.description && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {seq.description}
            </p>
          )}
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {stepCount} étape{stepCount > 1 ? "s" : ""}
          </p>
        </div>
      </div>
      <div className="flex flex-none items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          onClick={onEdit}
          className="h-7 gap-1 px-2 text-xs"
        >
          <Edit2 className="h-3 w-3" />
          Modifier
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={onDelete}
          className="h-7 w-7 text-muted-foreground hover:text-red-500"
          title="Supprimer"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function SequenceEditDialog({ open, onOpenChange, target, projet, onSaved }) {
  const isEdit = !!target;
  const [form, setForm] = useState(() => emptyForm(target, projet));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(emptyForm(target, projet));
  }, [open, target?.id, projet?.id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateStep = (idx, patch) =>
    setForm((f) => {
      const steps = [...f.steps];
      steps[idx] = { ...steps[idx], ...patch };
      return { ...f, steps };
    });
  const addStep = () =>
    setForm((f) => ({
      ...f,
      steps: [
        ...f.steps,
        {
          order: f.steps.length,
          delay_hours: f.steps.length === 0 ? 0 : 72,
          kind: "whatsapp_text",
          body: "",
          template_name: "",
          template_language: "fr",
          template_param_keys: ["prenom"],
          stop_if: ["status_won_or_lost", "lead_replied"],
        },
      ],
    }));
  const removeStep = (idx) =>
    setForm((f) => ({
      ...f,
      steps: f.steps
        .filter((_, i) => i !== idx)
        .map((s, i) => ({ ...s, order: i })),
    }));
  const moveStep = (idx, dir) =>
    setForm((f) => {
      const steps = [...f.steps];
      const j = idx + dir;
      if (j < 0 || j >= steps.length) return f;
      [steps[idx], steps[j]] = [steps[j], steps[idx]];
      return { ...f, steps: steps.map((s, i) => ({ ...s, order: i })) };
    });

  const save = async () => {
    if (!form.nom.trim()) {
      toast.error("Nom requis");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nom: form.nom.trim(),
        description: form.description.trim(),
        projet_id: form.projet_id || null,
        is_default: form.is_default,
        active: form.active,
        steps: form.steps.map((s, i) => ({ ...s, order: i })),
      };
      if (isEdit) {
        await sequencesApi.update(target.id, payload);
        toast.success("Séquence mise à jour");
      } else {
        await sequencesApi.create(payload);
        toast.success("Séquence créée");
      }
      onSaved?.();
    } catch (e) {
      const d = e?.response?.data?.detail || e?.message || "Échec";
      toast.error(d);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[700px] max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-violet-600" />
            {isEdit ? `Modifier la séquence — ${target.nom}` : "Nouvelle séquence"}
          </DialogTitle>
          <DialogDescription>
            Définis les étapes (envoi WhatsApp, note interne, attente) avec
            un délai en heures depuis le démarrage de la séquence.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Identité */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Nom</Label>
              <Input
                value={form.nom}
                onChange={(e) => update("nom", e.target.value)}
                placeholder="ex : Onboarding standard"
                data-testid="seq-nom"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description (optionnel)</Label>
              <Input
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                placeholder="À quoi sert cette séquence ?"
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label className="text-sm font-semibold">Active</Label>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Désactiver suspend la séquence sans supprimer les instances.
                  </p>
                </div>
                <Switch
                  checked={form.active}
                  onCheckedChange={(v) => update("active", v)}
                />
              </div>
            </div>
            <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label className="text-sm font-semibold">Séquence par défaut</Label>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Démarre automatiquement à la création d'un lead{form.projet_id ? "" : " (global)"}.
                  </p>
                </div>
                <Switch
                  checked={form.is_default}
                  onCheckedChange={(v) => update("is_default", v)}
                />
              </div>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-2 rounded-lg border border-border/60 bg-card p-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Étapes</Label>
              <Button size="sm" variant="outline" onClick={addStep} className="gap-1">
                <Plus className="h-3.5 w-3.5" />
                Ajouter une étape
              </Button>
            </div>
            {form.steps.length === 0 ? (
              <p className="rounded border border-dashed border-border/60 bg-muted/20 p-4 text-center text-xs text-muted-foreground">
                Aucune étape — clique « Ajouter une étape » pour commencer.
              </p>
            ) : (
              <div className="space-y-2">
                {form.steps.map((step, idx) => (
                  <StepRow
                    key={idx}
                    step={step}
                    idx={idx}
                    onChange={(patch) => updateStep(idx, patch)}
                    onRemove={() => removeStep(idx)}
                    onUp={() => moveStep(idx, -1)}
                    onDown={() => moveStep(idx, 1)}
                    isFirst={idx === 0}
                    isLast={idx === form.steps.length - 1}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4 flex-col gap-2 sm:flex-row">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="gap-1.5"
          >
            <X className="h-3.5 w-3.5" />
            Annuler
          </Button>
          <Button
            onClick={save}
            disabled={saving || !form.nom.trim()}
            className="gap-1.5 bg-violet-600 text-white hover:bg-violet-700"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? "Enregistrement…" : isEdit ? "Mettre à jour" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepRow({ step, idx, onChange, onRemove, onUp, onDown, isFirst, isLast }) {
  const isTemplate = step.kind === "whatsapp_template";
  const isText = step.kind === "whatsapp_text" || step.kind === "log_note";
  return (
    <div className="rounded-lg border border-border/60 bg-muted/10 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
          Étape {idx + 1}
        </span>
        <div className="flex gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={onUp}
            disabled={isFirst}
            className="h-6 w-6"
            title="Monter"
          >
            ↑
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={onDown}
            disabled={isLast}
            className="h-6 w-6"
            title="Descendre"
          >
            ↓
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={onRemove}
            className="h-6 w-6 text-muted-foreground hover:text-red-500"
            title="Supprimer"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <div className="grid gap-2 md:grid-cols-[120px_1fr]">
        <div className="space-y-1">
          <Label className="text-[10px]">Délai (heures)</Label>
          <Input
            type="number"
            min={0}
            value={step.delay_hours}
            onChange={(e) =>
              onChange({ delay_hours: parseInt(e.target.value, 10) || 0 })
            }
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Action</Label>
          <select
            value={step.kind}
            onChange={(e) => onChange({ kind: e.target.value })}
            className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs"
          >
            {STEP_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isTemplate && (
        <div className="mt-2 grid gap-2 md:grid-cols-[1fr_80px]">
          <div className="space-y-1">
            <Label className="text-[10px]">Nom du template Meta</Label>
            <Input
              value={step.template_name}
              onChange={(e) => onChange({ template_name: e.target.value })}
              placeholder="ex : sogi_relance_j3"
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px]">Langue</Label>
            <Input
              value={step.template_language || "fr"}
              onChange={(e) => onChange({ template_language: e.target.value })}
              placeholder="fr"
              className="h-8 text-xs"
            />
          </div>
        </div>
      )}

      {(isText || isTemplate) && (
        <div className="mt-2 space-y-1">
          <Label className="text-[10px]">
            {isText
              ? "Message"
              : "Aperçu interne (le destinataire reçoit le template Meta)"}
          </Label>
          <Textarea
            rows={2}
            value={step.body}
            onChange={(e) => onChange({ body: e.target.value })}
            placeholder={
              isText
                ? "Bonjour {prenom}, …"
                : "Texte d'aperçu stocké dans l'historique CRM"
            }
            className="text-xs"
          />
          <p className="text-[9px] text-muted-foreground">
            Placeholders : <code className="rounded bg-muted px-1">{"{nom}"}</code>,{" "}
            <code className="rounded bg-muted px-1">{"{prenom}"}</code>
          </p>
        </div>
      )}

      {/* Stop conditions */}
      <div className="mt-2 space-y-1">
        <Label className="text-[10px]">Conditions d'arrêt</Label>
        <div className="flex flex-wrap gap-2">
          {STOP_OPTIONS.map((opt) => {
            const checked = (step.stop_if || []).includes(opt.value);
            return (
              <label
                key={opt.value}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] ${
                  checked
                    ? "border-violet-300 bg-violet-100 text-violet-700 dark:border-violet-900/40 dark:bg-violet-950/40 dark:text-violet-300"
                    : "border-border bg-card text-muted-foreground"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = new Set(step.stop_if || []);
                    if (e.target.checked) next.add(opt.value);
                    else next.delete(opt.value);
                    onChange({ stop_if: Array.from(next) });
                  }}
                  className="h-3 w-3 accent-violet-600"
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function emptyForm(target, projet) {
  if (target) {
    return {
      nom: target.nom || "",
      description: target.description || "",
      projet_id: target.projet_id || projet?.id || null,
      is_default: !!target.is_default,
      active: target.active !== false,
      steps: Array.isArray(target.steps)
        ? target.steps.map((s) => ({
            order: s.order ?? 0,
            delay_hours: s.delay_hours ?? 0,
            kind: s.kind || "whatsapp_text",
            body: s.body || "",
            template_name: s.template_name || "",
            template_language: s.template_language || "fr",
            template_param_keys: Array.isArray(s.template_param_keys)
              ? s.template_param_keys
              : ["prenom"],
            stop_if: Array.isArray(s.stop_if)
              ? s.stop_if
              : ["status_won_or_lost"],
          }))
        : [],
    };
  }
  return {
    nom: "",
    description: "",
    projet_id: projet?.id || null,
    is_default: false,
    active: true,
    steps: [],
  };
}
